import { useTranslation } from '@framework/i18n';
import { Breadcrumbs, PageLayoutBase, SkipLinks } from '@framework/ui';
import type { ISkipLink } from '@framework/ui';
import { FrontFooterLayout } from '#theme';
import clx from 'classnames';
import React from 'react';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import { FrontLegalEntityBanner } from '../../components/legal-entity/FrontLegalEntityBanner';
import { PageLayoutHeader } from './header/PageLayoutHeader';

const PageContentWrapper = styled.div`
  padding: 12px 24px;
  padding-bottom: 24px;
  ${down('sm')} {
    padding: 12px 0;
  }
`;
const BreadcrumbsWrapper = styled.div`
  padding-bottom: 12px;
  max-width: 100%;
  ${down('sm')} {
    padding-left: 12px;
  }
`;

export interface PageLayoutProps {
  children: React.ReactNode;
  hasTopBar?: boolean;
  fixedHeader?: boolean;
  boxedContent?: boolean;
  noPadding?: boolean;
  className?: string;

  boxedHeader?: boolean;
  /** Pulsanti extra da mostrare in Header */
  extraButtons?: React.ReactNode;
  /** Lista di link aggiuntivi per accessibilità (skip links) */
  skipLinksAddiction?: ISkipLink[];
  /** per avere un content più esteso */
  largeContent?: boolean;
  variant?: 'default' | 'public';
}

/**
 * Layout principale dell'applicativo.
 */
export function PageLayout(props: PageLayoutProps) {
  const { t } = useTranslation();
  const {
    hasTopBar = true,
    boxedHeader = true,
    boxedContent = true,
    largeContent = false,
    noPadding = true,
    extraButtons,
    skipLinksAddiction,
    variant = 'default',
  } = props;

  const chatBotStyle = process.env.AI_CHATBOT_OPENK9_FRONTOFFICE_URL
    ? {
        style: {
          right: '100px',
          bottom: '30px',
        },
      }
    : undefined;

  const footerLink = hasTopBar
    ? [
        {
          label: t('layout.skip-links.vai-al-footer', 'Vai al footer'),
          htmlId: 'main-footer',
        },
      ]
    : [];
  const skipLinks = [
    ...(variant !== 'public'
      ? [
          {
            label: t(
              'layout.skip-links.vai-al-menu-principale',
              'Vai al menu principale',
            ),
            htmlId: 'main-menu',
          },
        ]
      : []),
    {
      label: t(
        'layout.skip-links.vai-al-contenuto-principale',
        'Vai al contenuto principale',
      ),
      htmlId: 'main-content',
    },
    ...(skipLinksAddiction ?? []),
    ...footerLink,
  ];

  return (
    <PageLayoutBase
      footer={hasTopBar && <FrontFooterLayout />}
      topBarHeader={
        hasTopBar && (
          <>
            <SkipLinks links={skipLinks} />
            <PageLayoutHeader
              fixed={props.fixedHeader}
              boxed={boxedHeader}
              extra={extraButtons}
              variant={variant}
            />
            {variant !== 'public' && <FrontLegalEntityBanner />}
          </>
        )
      }
      className={clx(props.className)}
      boxedContent={boxedContent}
      largeContent={largeContent}
      fixedHeader={props.fixedHeader}
      noPadding={noPadding}
      backTopProps={{
        ...chatBotStyle,
        'aria-label': t(
          'layout.footer.vai-all-inizio-pagina',
          'Vai all’inizio della pagina',
        ),
      }}
    >
      <PageContentWrapper>
        {hasTopBar && variant !== 'public' && (
          <BreadcrumbsWrapper>
            <Breadcrumbs />
          </BreadcrumbsWrapper>
        )}
        <main id="main-content">{props.children}</main>
      </PageContentWrapper>
    </PageLayoutBase>
  );
}
